import React,{useState} from 'react'

// Colors are the media kit's real background/text pairs (see
// "media kit 26_27/palettes color description.png"). Copy and the
// bold/italic pattern below are transcribed from the association's own
// poster designs for each activity (one slide per activity, same colors
// as here). Bold runs use the display font (League Spartan), italic runs
// use the secondary font (DM Serif Display Italic) — matching the two
// typefaces the posters themselves use.
//
// Idea Showcase & Feedback Session replaces what used to be two separate
// activities (Idea Jams + Demo Lite), using the purple/white pair freed up
// by that merge; its poster has no separate subtitle line, unlike the
// other three, so `subtitle` is omitted there.
//
// Clicking an item closes whichever was open and recolors the whole
// section to the new pair — same idea as the header's hover oval, just
// applied to the full section instead of a small pill.
const ACTIVITIES=[
    {
        name: 'Build Nights',
        bg: '#37d5d3',
        fg: '#3d5afe',
        subtitle: 'unstructured co-working',
        paragraphs: [
            [{b: 'A relaxed co-working evening where members can '},{i: 'work on their projects, discuss startup ideas, and exchange feedback with peers.'}],
            [{b: 'Bring a project, an idea, or simply your curiosity: '},{i: 'everyone is welcome, with no preparation or prior experience needed.'}],
            [{b: 'The board provides the space and dinner or snacks, while '},{i: 'members are free to work independently, brainstorm together, '},{b: 'or join a conversation.'}],
            [{b: 'With no fixed agenda, the goal is to make progress and meet potential collaborators, '},{i: 'in a welcoming, informal environment.'}],
        ],
    },
    {
        name: 'Workshops',
        bg: '#b9f227',
        fg: '#152238',
        subtitle: 'Practical skills sessions',
        paragraphs: [
            [{b: 'Each session combines a theoretical approach with practical examples, covering topics such as '},{i: 'how to structure an effective pitch, validate an idea, approach potential users, build an MVP, and so on.'}],
            [{b: 'The aim is to make entrepreneurial concepts '},{i: 'clear, concrete and applicable, '},{b: 'even for members with no experience.'}],
            [{b: 'Workshops will not only focus on success stories, but will also highlight the '},{i: 'trade-offs and common mistakes founders face along the way.'}],
            [{b: 'You will come away not only knowing what to do, but also understanding why '},{i: 'certain approaches work.'}],
        ],
    },
    {
        name: 'Speaker Sessions',
        bg: '#ff8a3d',
        fg: '#6b123a',
        subtitle: 'talks & conversations',
        paragraphs: [
            [{b: 'We will invite people who have turned ambition into action: '},{i: 'startup founders, professors, tech professionals, Bocconi alumni '},{b: 'whose careers have taken them to the heart of innovation.'}],
            [{b: 'Each session opens with a '},{i: 'talk on their professional path, '},{b: 'that includes the key turning points, the setbacks, and the lessons that shaped their journey.'}],
            [{b: 'The sessions conclude with '},{i: 'an open Q&A and time to chat with the speakers in person. '},{b: 'You might arrive with a question and leave with a new direction, or with someone to explore it with.'}],
        ],
    },
    {
        name: 'Idea Showcase & Feedback Session',
        bg: '#5b4bff',
        fg: '#ffffff',
        paragraphs: [
            [{b: 'A welcoming, low-pressure space to share '},{i: 'early-stage ideas, projects, or prototypes'}],
            [{b: "Whether you have a fully formed venture or only an idea, you'll have the chance to "},{i: "present what you're working on, hear constructive feedback, and connect "},{b: 'with people who want to help it grow.'}],
            [{b: 'Receive perspectives from other peers, '},{i: 'experienced founders, and industry leaders '},{b: 'who can offer practical insight, encouragement, and connections.'}],
            [{b: 'After the showcases, the session opens into '},{i: 'a casual social space '},{b: 'for deeper conversations, collaboration, and community-building.'}],
        ],
    },
]

function ActivityText({segments}) {
    return segments.map((seg,i) => seg.i? (
        <span key={i} className='italic font-serif-display'>{seg.i}</span>
    ):(
        <span key={i} className='font-semibold font-display'>{seg.b}</span>
    ))
}

export default function ActivitiesSection() {
    const [active,setActive]=useState(0)
    const current=ACTIVITIES[active]

    return (
        <section
            className='overflow-hidden py-20 w-full transition-colors duration-700 ease-out lg:py-28'
            style={{backgroundColor: current.bg}}
        >
            <div className='px-4 mx-auto max-w-4xl'>
                {ACTIVITIES.map((activity,i) => {
                    const isOpen=i===active
                    return (
                        <div key={activity.name} className='border-b transition-colors duration-700' style={{borderColor: current.fg+'33'}}>
                            <button
                                type='button'
                                onClick={() => setActive(i)}
                                className='flex justify-between items-center py-6 w-full text-left transition-colors duration-700'
                                style={{color: current.fg}}
                            >
                                <span
                                    className='font-bold transition-all duration-700 font-display'
                                    style={{
                                        fontSize: isOpen? 'clamp(2rem,5vw,3.5rem)':'clamp(1.25rem,2.5vw,1.75rem)',
                                        opacity: isOpen? 1:0.45,
                                    }}
                                >
                                    {activity.name}
                                </span>
                                <span className='text-2xl transition-opacity duration-700' style={{opacity: isOpen? 1:0.35}}>
                                    {isOpen? '−':'+'}
                                </span>
                            </button>
                            {isOpen&&(
                                <div className='pb-8 max-w-xl transition-colors duration-700' style={{color: current.fg}}>
                                    {activity.subtitle&&(
                                        <p className='text-lg italic opacity-80 font-serif-display lg:text-xl'>
                                            {activity.subtitle}
                                        </p>
                                    )}
                                    {activity.paragraphs.map((para,pi) => (
                                        <p key={pi} className={'text-base lg:text-lg '+(pi===0&&!activity.subtitle? '':'mt-4')}>
                                            <ActivityText segments={para} />
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>
        </section>
    )
}
